import React, { useEffect, useState, useRef, useCallback } from 'react';
import AsyncSelect from "react-select/async";
import ServiceReceipt from "./ServiceReceipt";
import { useNavigate } from "react-router-dom";
import { customFetch } from '../apiClient';

const getLocalISOTime = () => {
  const tzOffset = (new Date()).getTimezoneOffset() * 60000;
  return (new Date(Date.now() - tzOffset)).toISOString().slice(0, 16);
};

// Ise object ki jagah function bana diya gaya hai
const getInitialForm = () => ({
  s_IPD_ID: null,
  admdate: "",
  doctorID: "",
  servID: null,
  servRate: "",
  remarks: "",
  serviceName: "",
  amount: "",
  unit: "",
  qty: "1",
  servdate: getLocalISOTime()
});

const IPDServiceDetailForm = ({ onSubmitSuccess }) => {
 const [form, setForm] = useState(getInitialForm);
  const [loading, setLoading] = useState(false);
  const [patientDetails, setPatientDetails] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [savedData, setSavedData] = useState(null);
  const [addedServices, setAddedServices] = useState([]);
  const [selectedIpdPatient, setSelectedIpdPatient] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [editRowIndex, setEditRowIndex] = useState(null);
  const [editForm, setEditForm] = useState({
    qty: "",
    servRate: "",
    amount: "",
    unit: "",
    remarks: "",
  });
  const [touched, setTouched] = useState({});
  const [existingServices, setExistingServices] = useState([]);
  const [editingExistingId, setEditingExistingId] = useState(null);
  const [editingExistingData, setEditingExistingData] = useState({});

  const navigate = useNavigate();
  const formRef = useRef(null);
  const serviceSelectWrapperRef = useRef(null); 
  const qtyRef = useRef(null);
  const servdateRef = useRef(null);

  // Fetch company details and user
  useEffect(() => {
   
    const hospitalId = sessionStorage.getItem("hospitalId");

    if (hospitalId) {
      customFetch(`/Company/${hospitalId}`)
        .then(res => {
          if (!res.ok) throw new Error("Failed to load company details");
          return res.json();
        })
        .then(data => setCompanyDetails(data))
        .catch(console.error);
    }

    const user = sessionStorage.getItem("loggedInUser");
    if (user) setLoggedInUser(user);
  }, []);


  useEffect(() => {
    const intervalId = setInterval(() => {
      setForm(prev => {
        // Agar user ne date ko manually edit NAHI kiya hai, tabhi live time update karega
        if (!touched.servdate) {
          return { ...prev, servdate: getLocalISOTime() };
        }
        return prev;
      });
    }, 60000); // 60000 ms = 1 minute

    return () => clearInterval(intervalId);
  }, [touched.servdate]);

  // Auto-focus logic
  useEffect(() => {
    if (formRef.current) {
      const firstInput = formRef.current.querySelector(
        'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea, button:not([disabled])'
      );
      firstInput?.focus();
    }
  }, []);

  // Auto-dismiss alerts
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // --- Handlers for Edit, Calculation, API Calls ---
  const handleEditClick = (index) => {
    const srv = addedServices[index];
    setEditRowIndex(index);
    setEditForm({
      qty: srv.qty,
      servRate: srv.servRate,
      amount: srv.amount,
      unit: srv.unit,
      remarks: srv.remarks,
    });
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));

    if (name === "qty" || name === "servRate") {
      const qtyNum = name === "qty" ? parseFloat(value) : parseFloat(editForm.qty);
      const rateNum = name === "servRate" ? parseFloat(value) : parseFloat(editForm.servRate);
      if (!isNaN(qtyNum) && !isNaN(rateNum)) {
        setEditForm((prev) => ({
          ...prev,
          amount: (qtyNum * rateNum).toFixed(2),
        }));
      }
    }
  };

  const handleSaveEdit = () => {
    setAddedServices((prev) =>
      prev.map((srv, idx) =>
        idx === editRowIndex
          ? {
              ...srv,
              qty: Number(editForm.qty),
              servRate: Number(editForm.servRate),
              amount: Number(editForm.amount),
              unit: editForm.unit,
              remarks: editForm.remarks,
            }
          : srv
      )
    );
    setEditRowIndex(null);
  };

  const handleCancelEdit = () => {
    setEditRowIndex(null);
  };

  const loadIpdOptions = async (inputValue) => {
    if (!inputValue) return [];
   try {
      const res = await customFetch(`/Ipds/list?search=${encodeURIComponent(inputValue)}`);
      
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      
      return data.map((item) => {
        // ✅ Changed ipD_Date to regDate 
        const regDateFormatted = item.regDate 
            ? new Date(item.regDate).toLocaleDateString('en-IN', { 
                day: '2-digit', month: 'short', year: 'numeric' 
              }) 
            : "N/A";

        return {
          // ✅ Label updated to show Reg Date
          label: `${item.name} (${item.mobileNo}) | UHID: ${item.uhiD_No} | IPD No: ${item.ipD_SRNO} | Reg Date: ${regDateFormatted}`,
          value: item.ipD_ID,
          admDate: item.regDate, // Isko regDate se map kar diya taaki form mein save karte waqt yahi date jaye
          name: item.name,
        };
      });
    } catch { 
      return []; 
    }
  };

  const handleIpdChange = async (selectedOption) => {
    if (!selectedOption) {
      setForm(prev => ({ ...prev, s_IPD_ID: null, admdate: "", doctorID: "" }));
      setSelectedIpdPatient(null);
      setAddedServices([]);
      setExistingServices([]);
      return;
    }
    setForm(prev => ({
      ...prev,
      s_IPD_ID: selectedOption.value,
      admdate: selectedOption.admDate || "",
      doctorID: "",
    }));
    setSelectedIpdPatient(selectedOption);
    setAddedServices([]);
    setTouched(prev => ({ ...prev, s_IPD_ID: true }));
    
    // Load existing services for selected patient
    await loadExistingServices(selectedOption.value);
    
    setTimeout(() => {
        servdateRef.current?.focus();
    }, 100);
  };

  const loadServiceOptions = async (inputValue) => {
   try {
        const res = await customFetch(`/ChargeMaster/list?search=${encodeURIComponent(inputValue)}&type=IPD`);
        
        if (!res.ok) throw new Error("Failed to load services");
        const data = await res.json();
        
        return data.map((srv) => ({
            label: srv.chargeName || "(No Name)",
            value: srv.srno,
            amount: srv.amount,
            unit: srv.unit,
        }));
    } catch (err) {
        console.error("IPD Service Load Error:", err);
        return [];
    }
  };

  const handleServiceChange = (selectedOption) => {
    if (!selectedOption) {
      setForm((prev) => ({ ...prev, servID: null, servRate: "", serviceName: "", unit: "", qty: "1", amount: "" }));
      return;
    }
    setForm((prev) => ({
      ...prev,
      servID: selectedOption.value,
      servRate: selectedOption.amount,
      serviceName: selectedOption.label,
      unit: selectedOption.unit,
      qty: "1",
      amount: selectedOption.amount?.toFixed(2) || "",
    }));
    setTouched(prev => ({ ...prev, servID: true }));
    setTimeout(() => {
        qtyRef.current?.focus();
        qtyRef.current?.select();
    }, 100);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setTouched(prev => ({ ...prev, [name]: true }));
  };

  const handleAddService = () => {
    if (!form.servID) { setError("Please select a service."); return; }
    if (!form.qty || Number(form.qty) <= 0) { setError("Quantity must be > 0."); return; }
    setError(""); setSuccess("");

    const newService = {
      servID: form.servID,
      serviceName: form.serviceName,
      qty: Number(form.qty),
      servRate: Number(form.servRate),
      amount: Number(form.amount),
      unit: form.unit,
      remarks: form.remarks,
    };
    setAddedServices((prev) => [...prev, newService]);
    setForm((prev) => ({ ...prev, servID: null, servRate: "", serviceName: "", unit: "", qty: "1", amount: "", remarks: "", servdate: prev.servdate }));
    setTimeout(() => {
        const input = serviceSelectWrapperRef.current?.querySelector('input');
        input?.focus();
    }, 100);
  };

  const handleRemoveService = (index) => {
    setAddedServices((prev) => prev.filter((_, idx) => idx !== index));
  };

  const loadExistingServices = async (ipdId) => {
   try {
      const res = await customFetch(`/IPDServiceDetailDT/by-ipd/${ipdId}`);
      if (res.ok) {
        const data = await res.json();
        setExistingServices(data);
      } else {
        setExistingServices([]);
      }
    } catch {
      setExistingServices([]);
    }
  };

  const startEditingExisting = (service) => {
    setEditingExistingId(service.servSrNo);
    const servdateValue = service.servdate ? new Date(service.servdate).toISOString().slice(0, 16) : "";
    setEditingExistingData({
      qty: service.qty || 1,
      servRate: service.servRate || 0,
      amount: service.amount || 0,
      remarks: service.remarks || "",
      servdate: servdateValue,
    });
  };

  const handleExistingChange = (field, value) => {
    setEditingExistingData((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === "qty" || field === "servRate") {
        const qtyNum = field === "qty" ? parseFloat(value) : parseFloat(prev.qty);
        const rateNum = field === "servRate" ? parseFloat(value) : parseFloat(prev.servRate);
        if (!isNaN(qtyNum) && !isNaN(rateNum)) {
          updated.amount = (qtyNum * rateNum).toFixed(2);
        }
      }
      return updated;
    });
  };

  const saveExistingService = async () => {
    if (!editingExistingId) return;
    
    setLoading(true);
    try {
     
      const serviceToUpdate = existingServices.find(s => s.servSrNo === editingExistingId);
      
      const res = await customFetch(`/IPDServiceDetailDT/${editingExistingId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          ...serviceToUpdate,
          qty: Number(editingExistingData.qty),
          servRate: Number(editingExistingData.servRate),
          amount: Number(editingExistingData.amount),
          remarks: editingExistingData.remarks,
          servdate: editingExistingData.servdate,
        }),
      });

      if (res.ok) {
        setExistingServices(prev => prev.map(s => 
          s.servSrNo === editingExistingId 
            ? { ...s, ...editingExistingData, qty: Number(editingExistingData.qty), servRate: Number(editingExistingData.servRate), amount: Number(editingExistingData.amount) }
            : s
        ));
        setEditingExistingId(null);
        setSuccess("Service updated successfully");
      } else {
        setError("Failed to update service");
      }
    } catch {
      setError("Network error");
    } finally {
      setLoading(false);
    }
  };

  const deleteExistingService = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    
    setLoading(true);
   try {
      const res = await customFetch(`/IPDServiceDetailDT/${id}`, {
        method: "DELETE"
      });

      if (res.ok) {
        setExistingServices(prev => prev.filter(s => s.servSrNo !== id));
        setSuccess("Service deleted successfully");
      } else {
        setError("Failed to delete service");
      }
    } catch {
      setError("Network error");
    } finally {
      setLoading(false);
    }
  };

  const handleGlobalKeyDown = useCallback((e) => {
    if (e.target.closest('.service-search-container')) {
      const inputVal = e.target.value;
      if (e.key === 'Enter') {
        if (!inputVal && !form.servID) {
          e.preventDefault();
          const submitBtn = formRef.current.querySelector('button[type="submit"]');
          submitBtn?.focus();
          return;
        }
        return; 
      }
    }

    const focusable = formRef.current.querySelectorAll(
      'input:not([disabled]):not([readonly]):not([type="hidden"]), ' +
      'select:not([disabled]), ' +
      'button:not([disabled]):not(.btn-icon):not(.btn-table), ' + 
      'textarea:not([disabled])'
    );

    const elementsArr = Array.from(focusable).filter(el => !el.closest('table'));
    const activeIndex = elementsArr.indexOf(document.activeElement);

    if (e.key === 'Enter') {
      if (document.activeElement.classList.contains('btn-add-service')) {
        e.preventDefault();
        handleAddService();
        return;
      }
      if (document.activeElement.type === 'submit') {
        return;
      }
      e.preventDefault(); 
      const nextIndex = activeIndex + 1;
      if (nextIndex < elementsArr.length) {
        elementsArr[nextIndex].focus();
        if (elementsArr[nextIndex].tagName === 'INPUT') {
          elementsArr[nextIndex].select(); 
        }
      }
    } else if (e.key === 'ArrowUp') {
       e.preventDefault();
       const prevIndex = Math.max(0, activeIndex - 1);
       elementsArr[prevIndex]?.focus();
    }
  }, [form.servID, addedServices, form.qty, form.servRate, form.unit, form.remarks]); 

  useEffect(() => {
    const handler = (e) => {
      if (formRef.current && formRef.current.contains(e.target)) {
        handleGlobalKeyDown(e);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [handleGlobalKeyDown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.s_IPD_ID) { setError("Please select an IPD patient."); return; }
    if (addedServices.length === 0) { setError("Please add services."); return; }

    setError(""); setSuccess(""); setLoading(true);

    try {
     
      const hospitalId = sessionStorage.getItem("hospitalId");

      const payload = addedServices.map((service) => {
        let formattedServDate = form.servdate || getLocalISOTime();
        if (formattedServDate.includes(" ")) formattedServDate = formattedServDate.replace(" ", "T");

        let formattedAdmDate = form.admdate || new Date().toISOString();
        if (formattedAdmDate.includes(" ")) formattedAdmDate = formattedAdmDate.replace(" ", "T");

        return {
          servSrNo: 0,
          srno: 0,
          s_IPD_ID: Number(form.s_IPD_ID), 
          admdate: formattedAdmDate, 
          servID: Number(service.servID), 
          qty: Number(service.qty),
          servRate: Number(service.servRate),
          remarks: service.remarks || "",
          serviceName: service.serviceName || "",
          amount: Number(service.amount),
          hid: Number(hospitalId),
          syncSts: false,
          unit: service.unit || "",
          servdate: formattedServDate 
        };
      });

      const res = await customFetch(`/IPDServiceDetailDT/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Failed to save service details");
      const data = await res.json();
      
      setSuccess("Services saved successfully");
      setAddedServices([]);
      setForm(getInitialForm());
      setTouched({});

      setSelectedIpdPatient(null);
      setSavedData(data.savedDetails || []);
      setPatientDetails(data.patientDetails || null);
      setShowReceipt(true);
      if (onSubmitSuccess) onSubmitSuccess(data);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

const handleButtonClick = () => { navigate('/clinical/service-table'); }; // ✅ Clinical Layout me bhejega
  const getTotalAmount = () => addedServices.reduce((sum, srv) => sum + srv.amount, 0).toFixed(2);

  // SCALED DOWN COMPACT REACT SELECT STYLES
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '30px', /* Reduced height */
      fontSize: '11px',  /* Reduced font */
      borderRadius: '6px',
      borderColor: touched.s_IPD_ID && !form.s_IPD_ID 
        ? '#e74c3c' 
        : state.isFocused 
          ? 'var(--theme-color, #0088cc)' 
          : '#cbd5e0',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(0, 0, 0, 0.05)' : 'none',
      '&:hover': { borderColor: 'var(--theme-color, #0088cc)' }
    }),
    valueContainer: (base) => ({ ...base, padding: '0 6px' }),
    input: (base) => ({ ...base, margin: '0', padding: '0' }),
    dropdownIndicator: (base) => ({ ...base, padding: '4px' }),
    clearIndicator: (base) => ({ ...base, padding: '4px' }),
    option: (base, state) => ({
      ...base,
      fontSize: '11px', /* Reduced font */
      padding: '6px 10px',
      backgroundColor: state.isSelected 
        ? 'var(--theme-color, #0088cc)' 
        : state.isFocused 
          ? 'color-mix(in srgb, var(--theme-color, #0088cc), white 90%)'
          : 'white',
      color: state.isSelected ? 'white' : '#2c3e50',
      cursor: 'pointer',
      '&:active': { backgroundColor: 'var(--theme-color, #0088cc)' },
    }),
    placeholder: (base) => ({ ...base, color: '#a0aec0', fontSize: '11px' }),
    menu: (base) => ({ ...base, zIndex: 9999, borderRadius: '6px', fontSize: '11px' })
  };

  return (
    <div className="service-detail-container compact-ui">
      <div className="service-card">
        {/* Header */}
        <div className="service-header">
          <div className="header-left">
            <div className="header-icon">
              <i className="bi bi-clipboard2-pulse"></i>
            </div>
            <div className="header-text">
              <h5>IPD Service Management</h5>
              <p>Add and manage services for admitted patients</p>
            </div>
          </div>
          <button className="btn-view-list" onClick={handleButtonClick} title="Edit Services">
            <i className="bi bi-pencil-square"></i>
            Edit Services
          </button>
        </div>

        {/* Alerts */}
        {error && (
          <div className="alert-custom alert-danger slide-in">
            <i className="bi bi-exclamation-triangle-fill"></i>
            <span>{error}</span>
            <button className="alert-close" onClick={() => setError("")}><i className="bi bi-x"></i></button>
          </div>
        )}
        {success && (
          <div className="alert-custom alert-success slide-in">
            <i className="bi bi-check-circle-fill"></i>
            <span>{success}</span>
            <button className="alert-close" onClick={() => setSuccess("")}><i className="bi bi-x"></i></button>
          </div>
        )}

        {/* Main Content */}
        <div className="main-content">
          {/* Top Panel - Form */}
          <div className="form-panel">
            <form ref={formRef} onSubmit={handleSubmit} className="service-form">
          <div className="form-section">
            <div className="section-title">
              <i className="bi bi-plus-circle"></i>
              <span>New Service Entry</span>
            </div>

            <div className="section-content">
              {/* Patient, Date & Service Selection */}
              <div className="form-row three-col">
                <div className="form-group">
                  <label>
                    <i className="bi bi-person-search"></i> Select IPD Patient <span className="required">*</span>
                  </label>
                  <AsyncSelect
                    cacheOptions
                    defaultOptions
                    loadOptions={loadIpdOptions}
                    onChange={handleIpdChange}
                    placeholder="Search patient..."
                    isClearable
                    tabIndex={1}
                    value={selectedIpdPatient}
                    isDisabled={loading}
                    styles={selectStyles}
                  />
                </div>

                <div className="form-group">
                  <label>
                    <i className="bi bi-calendar-event"></i> Service Date <span className="required">*</span>
                  </label>
                  <input
                    ref={servdateRef}
                    type="datetime-local"
                    name="servdate"
                    value={form.servdate}
                    onChange={handleChange}
                    className="form-control-custom"
                    tabIndex={2}
                    disabled={loading}
                    required
                  />
                </div>

                <div className="form-group service-search-container" ref={serviceSelectWrapperRef}>
                  <label>
                    <i className="bi bi-heart-pulse"></i> Service <span className="required">*</span>
                  </label>
                  <AsyncSelect
                    cacheOptions
                    defaultOptions
                    loadOptions={loadServiceOptions}
                    onChange={handleServiceChange}
                    tabIndex={3}
                    placeholder="Search service..."
                    isClearable
                    value={form.servID ? { value: form.servID, label: form.serviceName } : null}
                    isDisabled={loading}
                    styles={selectStyles}
                  />
                </div>
              </div>

              {/* Service Details */}
              <div className="form-row five-col">
                <div className="form-group">
                  <label><i className="bi bi-123"></i> Quantity</label>
                  <input
                  ref={qtyRef}
                    type="number"
                    name="qty"
                    value={form.qty}
                    tabIndex={4}
                    onChange={(e) => {
                      const qtyVal = parseFloat(e.target.value);
                      const rateVal = parseFloat(form.servRate);
                      setForm((prev) => ({
                        ...prev,
                        qty: e.target.value,
                        amount: !isNaN(qtyVal) && !isNaN(rateVal) ? (qtyVal * rateVal).toFixed(2) : "",
                      }));
                    }}
                    className="form-control-custom"
                    placeholder="1"
                    step="0.1"
                    min="0.1"
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label><i className="bi bi-currency-rupee"></i> Rate</label>
                  <input
                    type="number"
                    name="servRate"
                    tabIndex={5}
                    value={form.servRate}
                    onChange={(e) => {
                      const rateVal = parseFloat(e.target.value);
                      const qtyVal = parseFloat(form.qty);
                      setForm((prev) => ({
                        ...prev,
                        servRate: e.target.value,
                        amount: !isNaN(qtyVal) && !isNaN(rateVal) ? (qtyVal * rateVal).toFixed(2) : "",
                      }));
                    }}
                    className="form-control-custom"
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label><i className="bi bi-rulers"></i> Unit</label>
                  <input
                    type="text"
                    name="unit"
                    value={form.unit}
                    tabIndex={6}
                    onChange={e => setForm(prev => ({ ...prev, unit: e.target.value }))}
                    className="form-control-custom"
                    placeholder="Pcs, Mg"
                    disabled={loading}
                    maxLength={30}
                  />
                </div>

                <div className="form-group">
                  <label><i className="bi bi-calculator"></i> Amount</label>
                  <input
                    type="number"
                    name="amount"
                    value={form.amount}
                    tabIndex={7}
                    className="form-control-custom"
                    placeholder="0.00"
                    disabled={loading}
                    readOnly
                  />
                </div>

                <div className="form-group ">
                  <label><i className="bi bi-chat-left-text"></i> Remarks</label>
                  <input
                    type="text"
                    name="remarks"
                    tabIndex={8}
                    value={form.remarks}
                    onChange={handleChange}
                    placeholder="Comments..."
                    className="form-control-custom"
                    disabled={loading}
                    maxLength={100}
                  />
                </div>
              </div>

              <div className="add-service-row">
                <button
                  type="button"
                  className="btn-add-service"
                  onClick={handleAddService}
                  tabIndex={9}
                  disabled={loading || !form.servID}
                >
                  <i className="bi bi-plus-circle"></i> Add Service
                </button>
              </div>
            </div>
          </div>

          {/* Services Table */}
          {addedServices.length > 0 && (
            <div className="table-section">
              <div className="table-header-custom">
                <div className="table-title">
                  <i className="bi bi-list-check"></i>
                  <span>Added Services</span>
                  <span className="badge-count">{addedServices.length}</span>
                </div>
                <div className="total-amount">
                  <span>Total:</span>
                  <span className="amount-value">₹{getTotalAmount()}</span>
                </div>
              </div>

              <div className="table-responsive">
                <table className="custom-table">
                  <thead>
                    <tr>
                      <th>Service Name</th>
                      <th className="text-center">Qty</th>
                      <th className="text-center">Rate</th>
                      <th className="text-center">Amount</th>
                      <th className="text-center">Unit</th>
                      <th>Remarks</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {addedServices.map((srv, idx) => (
                      <tr key={idx}>
                        <td className="fw-medium">{srv.serviceName}</td>
                        {editRowIndex === idx ? (
                          <>
                             {/* Edit Inputs */}
                             <td><input type="number" name="qty" value={editForm.qty} onChange={handleEditFormChange} className="form-control form-control-sm" /></td>
                             <td><input type="number" name="servRate" value={editForm.servRate} onChange={handleEditFormChange} className="form-control form-control-sm" /></td>
                             <td><input type="number" name="amount" value={editForm.amount} readOnly className="form-control form-control-sm" /></td>
                             <td><input type="text" name="unit" value={editForm.unit} onChange={handleEditFormChange} className="form-control form-control-sm" /></td>
                             <td><input type="text" name="remarks" value={editForm.remarks} onChange={handleEditFormChange} className="form-control form-control-sm" /></td>
                             <td className="text-center">
                               <button className="btn-table btn-save" type="button" onClick={handleSaveEdit}><i className="bi bi-check-circle"></i> Save</button>
                               <button className="btn-table btn-cancel" type="button" onClick={handleCancelEdit}><i className="bi bi-x-circle"></i> Cancel</button>
                             </td>
                          </>
                        ) : (
                          <>
                            <td className="text-center">{srv.qty}</td>
                            <td className="text-center">₹{srv.servRate.toFixed(2)}</td>
                            <td className="text-center fw-semibold text-success">₹{srv.amount.toFixed(2)}</td>
                            <td className="text-center">{srv.unit}</td>
                            <td className="text-muted small">{srv.remarks || '-'}</td>
                            <td className="text-center action-buttons">
                              <button className="btn-icon btn-edit" onClick={() => handleEditClick(idx)} type="button"><i className="bi bi-pencil-square"></i></button>
                              <button className="btn-icon btn-delete" onClick={() => handleRemoveService(idx)} type="button"><i className="bi bi-trash"></i></button>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="submit"
              className="btn-submit"
              tabIndex={20}
              disabled={loading || !form.s_IPD_ID || addedServices.length === 0}
            >
              {loading ? <><span className="spinner-border spinner-border-sm me-2"></span>Saving...</> : <><i className="bi bi-check-circle"></i> Submit All Services</>}
            </button>
          </div>
        </form>
          </div>

          {/* Bottom Panel - Existing Services */}
          {selectedIpdPatient && (
            <div className="existing-services-panel">
              <div className="existing-services-section">
                <div className="section-title">
                  <i className="bi bi-list-ul"></i>
                  <span>Existing Services</span>
                  {existingServices.length > 0 && (
                    <span className="badge-count">{existingServices.length}</span>
                  )}
                </div>
                
                <div className="existing-services-content">
                  {existingServices.length === 0 ? (
                    <div className="empty-state-small">
                      <i className="bi bi-inbox"></i>
                      <p>No existing services found</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="custom-table">
                        <thead>
                          <tr>
                            <th>Service</th>
                            <th>Date</th>
                            <th>Qty</th>
                            <th>Rate</th>
                            <th>Amount</th>
                            <th className="text-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {existingServices.map((service) => (
                            <tr key={service.servSrNo}>
                              <td className="fw-medium">{service.serviceName}</td>
                              
                              {editingExistingId === service.servSrNo ? (
                                <>
                                  <td>
                                    <input
                                      type="datetime-local"
                                      className="form-control form-control-sm"
                                      value={editingExistingData.servdate || ""}
                                      onChange={(e) => handleExistingChange("servdate", e.target.value)}
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      className="form-control form-control-sm"
                                      value={editingExistingData.qty || ""}
                                      onChange={(e) => handleExistingChange("qty", e.target.value)}
                                      min="1" step="0.1"
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      className="form-control form-control-sm"
                                      value={editingExistingData.servRate || ""}
                                      onChange={(e) => handleExistingChange("servRate", e.target.value)}
                                      min="0" step="0.01"
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      className="form-control form-control-sm"
                                      value={editingExistingData.amount || ""}
                                      readOnly
                                    />
                                  </td>
                                  <td className="text-center action-buttons">
                                    <button className="btn-icon btn-save" onClick={saveExistingService} disabled={loading}>
                                      <i className="bi bi-check"></i>
                                    </button>
                                    <button className="btn-icon btn-cancel" onClick={() => setEditingExistingId(null)} disabled={loading}>
                                      <i className="bi bi-x"></i>
                                    </button>
                                  </td>
                                </>
                              ) : (
                                <>
                                  <td className="small">
                                    {service.servdate ? new Date(service.servdate).toLocaleDateString('en-IN') : '-'}
                                  </td>
                                  <td className="text-center">{service.qty}</td>
                                  <td className="text-center">₹{service.servRate}</td>
                                  <td className="text-center fw-semibold text-success">₹{service.amount}</td>
                                  <td className="text-center action-buttons">
                                    <button className="btn-icon btn-edit" onClick={() => startEditingExisting(service)}>
                                      <i className="bi bi-pencil"></i>
                                    </button>
                                    <button className="btn-icon btn-delete" onClick={() => deleteExistingService(service.servSrNo)}>
                                      <i className="bi bi-trash"></i>
                                    </button>
                                  </td>
                                </>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Receipt Modal */}
      {companyDetails && (
        <ServiceReceipt
          show={showReceipt}
          data={savedData}
          patient={patientDetails}
          companyDetails={companyDetails}
          loggedInUser={loggedInUser}
          onClose={() => setShowReceipt(false)}
          onPrint={() => window.print()}
        />
      )}

    {/* SCALED DOWN COMPACT UI CSS */}
      <style jsx>{`
        .compact-ui {
          font-family: system-ui, -apple-system, sans-serif;
        }

        .service-detail-container {
          padding: 0;
          max-height: 100vh;
          overflow-y: auto;
        }

        .service-card {
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
          border: 1px solid #e0e6ed;
          /* overflow: visible kiya taaki React Select na chhupe */
          overflow: visible; 
        }

        @keyframes slideIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .slide-in { animation: slideIn 0.2s ease; }

        .service-header {
          background: var(--theme-color, #0088cc);
          padding: 10px 16px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 2px solid rgba(0,0,0,0.1); 
          border-top-left-radius: 8px;
          border-top-right-radius: 8px;
        }

        .header-left { display: flex; align-items: center; gap: 10px; }

        .header-icon {
          width: 32px; height: 32px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 6px;
          display: flex; align-items: center; justify-content: center;
          font-size: 16px; color: white;
        }

        .header-text h5 { margin: 0; color: white; font-size: 15px; font-weight: 600; line-height: 1.1; }
        .header-text p { margin: 0; color: rgba(255, 255, 255, 0.85); font-size: 10px; }

        .btn-view-list {
          padding: 6px 12px;
          border-radius: 6px;
          border: 1.5px solid rgba(255, 255, 255, 0.3);
          background: rgba(255, 255, 255, 0.15);
          color: white;
          font-size: 11px; font-weight: 600; cursor: pointer;
          transition: all 0.2s ease;
          display: flex; align-items: center; gap: 5px;
        }

        .btn-view-list:hover {
          background: white;
          color: var(--theme-color, #0088cc);
          transform: translateY(-1px);
        }

        /* Alerts */
        .alert-custom {
          margin: 10px 16px 0; padding: 8px 12px; border-radius: 6px;
          display: flex; align-items: center; gap: 8px; font-size: 11px; font-weight: 500; position: relative;
        }
        .alert-danger { background: #fee; color: #c33; border: 1px solid #fcc; }
        .alert-success { background: #d4edda; color: #155724; border: 1px solid #c3e6cb; }
        .alert-close { position: absolute; right: 8px; background: transparent; border: none; cursor: pointer; opacity: 0.6; }

        /* Main Content Layout */
        .main-content {
          display: flex; flex-direction: column; gap: 12px; padding: 12px 16px;
        }
        .form-panel, .existing-services-panel { width: 100%; }

        .existing-services-section {
          background: #f8f9fa; border: 1px solid #e0e6ed; border-radius: 6px; overflow: hidden;
        }
        .existing-services-content {
          padding: 12px; background: white; max-height: 350px; overflow-y: auto;
        }

        .form-section {
          background: #f8f9fa; border: 1px solid #e0e6ed; border-radius: 8px; margin-bottom: 12px;
          /* overflow: visible kiya taaki React Select na chhupe */
          overflow: visible; 
        }

        .section-title {
          background: var(--theme-color, #0088cc); padding: 8px 12px;
          display: flex; align-items: center; gap: 6px;
          border-top-left-radius: 8px; border-top-right-radius: 8px;
        }
        .section-title i { font-size: 13px; color: white; }
        .section-title span { font-size: 12px; font-weight: 600; color: white; flex: 1; }

        .section-content { padding: 12px; background: white; border-bottom-left-radius: 8px; border-bottom-right-radius: 8px;}

        .form-row { display: grid; gap: 10px; margin-bottom: 10px; }
        .form-row:last-child { margin-bottom: 0; }
        .form-row.three-col { grid-template-columns: 2fr 1fr 2fr; }
        .form-row.five-col { grid-template-columns: repeat(5, 1fr); }

        .form-group { display: flex; flex-direction: column; }
        .form-group label {
          font-size: 11px; font-weight: 600; color: #2c3e50; margin-bottom: 4px; display: flex; align-items: center; gap: 4px;
        }
        .form-group label i { color: var(--theme-color, #0088cc); font-size: 10px; }
        .required { color: #e74c3c; }

        /* Input Styles */
        .form-control-custom {
          width: 100%; padding: 6px 10px; border: 1.5px solid #cbd5e0; border-radius: 6px; font-size: 11px;
          transition: all 0.2s; outline: none; background: white; height: 30px;
        }
        .form-control-custom:focus { border-color: var(--theme-color, #0088cc); box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.05); }
        .form-control-custom:disabled { background: #f3f4f6; opacity: 0.6; }
        .form-control-sm { padding: 4px 6px; height: 26px; }

        .add-service-row { margin-top: 8px; display: flex; justify-content: flex-end; }
        .btn-add-service {
          padding: 6px 16px; border-radius: 6px; border: none; background: var(--theme-color, #0088cc); 
          color: white; font-size: 11px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 5px;
        }
        .btn-add-service:hover:not(:disabled) { transform: translateY(-1px); opacity: 0.9; }

        /* Added Table Styles */
        .table-section {
          background: white; border-radius: 6px; padding: 12px; border: 1px solid #e0e6ed; margin-bottom: 12px;
        }
        .table-header-custom {
          display: flex; justify-content: space-between; align-items: center; padding-bottom: 6px; border-bottom: 1px solid #e0e6ed; margin-bottom: 8px;
        }
        .table-title { display: flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 600; color: #2c3e50; }
        .badge-count { background: var(--theme-color, #0088cc); color: white; padding: 2px 6px; border-radius: 10px; font-size: 10px; }
        .total-amount { display: flex; align-items: center; gap: 6px; font-size: 12px; }
        .amount-value { font-weight: 700; color: #27ae60; }

        .table-responsive { overflow-x: auto; }
        .custom-table { width: 100%; border-collapse: collapse; font-size: 11px; }
        .custom-table thead { background: var(--theme-color, #0088cc); }
        .custom-table thead th {
          color: white; font-weight: 600; padding: 6px 8px; font-size: 10px; text-transform: uppercase; border: none;
        }
        .custom-table tbody tr { border-bottom: 1px solid #e0e6ed; }
        .custom-table tbody tr:hover { background: #f8f9fa; }
        .custom-table tbody td { padding: 6px 8px; vertical-align: middle; }

        .text-center { text-align: center; }
        .fw-medium { font-weight: 500; }
        .fw-semibold { font-weight: 600; }
        .text-success { color: #27ae60; }
        .text-muted { color: #9ca3af; }
        .small { font-size: 10px; }
        
        .empty-state-small { text-align: center; padding: 1.5rem 1rem; color: #9ca3af; }
        .empty-state-small i { font-size: 2rem; margin-bottom: 0.3rem; display: block; }

        /* Buttons */
        .action-buttons { display: flex; gap: 0.4rem; justify-content: center; }
        .btn-icon {
          width: 24px; height: 24px; border: none; border-radius: 4px; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s; font-size: 11px;
        }
        .btn-edit { background: #dbeafe; color: #1e40af; }
        .btn-edit:hover { background: #1e40af; color: white; transform: translateY(-1px); }
        .btn-delete { background: #fee2e2; color: #dc2626; }
        .btn-delete:hover { background: #dc2626; color: white; transform: translateY(-1px); }
        .btn-save { background: #d1fae5; color: #065f46; }
        .btn-save:hover { background: #059669; color: white; transform: translateY(-1px); }
        .btn-cancel { background: #f3f4f6; color: #4b5563; }
        .btn-cancel:hover { background: #6b7280; color: white; transform: translateY(-1px); }
        
        .btn-table { padding: 3px 8px; border-radius: 4px; font-size: 10px; font-weight: 600; border: none; margin: 0 2px; }

        .form-actions { display: flex; justify-content: flex-end; margin-top: 12px; padding-top: 12px; border-top: 1px solid #e0e6ed; }
        .btn-submit {
          padding: 8px 24px; border-radius: 6px; border: none; background: #27ae60; color: white; font-size: 12px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 6px;
        }
        .btn-submit:hover:not(:disabled) { background: #219150; transform: translateY(-1px); }
        .btn-submit:disabled { opacity: 0.6; cursor: not-allowed; }

        @media (max-width: 768px) {
          .service-header { flex-direction: column; gap: 8px; }
          .form-row.three-col, .form-row.five-col { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

export default IPDServiceDetailForm;